import { UserActivationResolver } from 'app/auth/user-activation.resolver';
import { SsnMaskedComponent } from './shared/components/ssn-masked/ssn-masked.component';
import { DialogService } from './shared/services/dialog-service';
import { DialogComponent } from "app/shared/components/dialog/dialog.component";
import { ConfirmDialogComponent } from "app/shared/components/dialog/confirm-dialog.component";
import { CardConfirmService } from './shared/services/card-confirm.service';
import { CardDialogComponent } from "./shared/components/dialog/card-dialog.component";
import { StageQuestionsService } from 'app/stages/stage-questions.service';
import { EnrollmentProgramsService, EnrollmentService } from './shared/services/enrollment-programs.service';
import { AuthGuard } from './auth/auth.guard';
import { LoadingSpinnerService } from './shared/services/loading-spinner.service';
import { AuthModule } from './auth/auth.module';
import { SecurityModule } from './security/security.module';
import { SharedModule } from './shared/shared.module';

import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import {
  NgModule,
  ApplicationRef,
  APP_INITIALIZER
} from '@angular/core';
import {
  removeNgStyles,
  createNewHosts,
  createInputTransfer
} from '@angularclass/hmr';
import {
  RouterModule,
  PreloadAllModules
} from '@angular/router';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

/*
 * Platform and Environment providers/directives/pipes
 */
import { ENV_PROVIDERS } from './environment';
import { ROUTES } from './app.routes';
// App is our top level component
import { AppComponent } from './app.component';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { AppState, InternalStateType } from './app.service';
import { NoContentComponent } from './no-content';
import {ToastModule} from 'ng2-toastr/ng2-toastr';
import { HttpClientModule } from '@angular/common/http';

import '../styles/styles.scss';
import '../styles/headings.css';
import { StagesComponent } from './stages/stages.component';
import { StageComponent } from './stages/stage/stage.component';
import { AdditionalResourcesComponent } from './additional-resources/additional-resources.component';
import { StudentFileUploadComponents } from './additional-resources/student-file-uploads/student-file-uploads.component';
import { PaymentMethodsComponent } from './additional-resources/payment-methods/payment-methods.component';
import { UserResolver } from './auth/user.resolver';
import { SummaryStageComponent } from './stages/summary-stage/summary-stage.component';
import { AppStatementsResolver } from './shared/app-statements.resolver';
import { LaunchResolver } from 'app/auth/launch.resolver';
import { StartupService } from 'app/shared/services/startup.service';
import { CanDeactivateStage } from 'app/stages/can-deactivate-stage';
import { ThankyouComponent } from './stages/thankyou-stage/thankyou-stage.component';
import { PrintLayoutComponent } from './stages/print-layout/print-layout.component';
import { PrintService } from './shared/services/print.service';
import { PrintGuard } from './shared/services/print.guard';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { GoogleAnalyticsService } from './google-analytics.service';
import { ContractsService } from "app/shared/services/contracts.service";


// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
  AppState
];

type StoreType = {
  state: InternalStateType,
  restoreInputValues: () => void,
  disposeOldHosts: () => void
};


export function startupServiceFactory(startupSer:StartupService): Function {
  return  ()=>startupSer.setup();
}

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({

  bootstrap: [ AppComponent ],
  declarations: [
    AppComponent,
    AdditionalResourcesComponent,
    NoContentComponent,
    StagesComponent,
    DialogComponent,
    ConfirmDialogComponent,
    CardDialogComponent,
    StageComponent,
    StudentFileUploadComponents,
    PaymentMethodsComponent,
    SummaryStageComponent,
    ThankyouComponent,
    PrintLayoutComponent    
  ],
  /**
   * Import Angular's modules.
   */
  entryComponents:[
    DialogComponent,
    ConfirmDialogComponent,
    CardDialogComponent,
  ],
  imports: [    
    BrowserModule,
    BrowserAnimationsModule,    
    HttpModule,
    NgxDropzoneModule,
    RouterModule.forRoot(ROUTES, {
      useHash: Boolean(history.pushState) === false,
      preloadingStrategy: PreloadAllModules
    }),
    SharedModule,    
    SecurityModule,    
    ToastModule.forRoot(),
    HttpClientModule,
    AuthModule.forRoot()
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    
    StartupService,
    {
        // Provider for APP_INITIALIZER
        provide: APP_INITIALIZER,
        useFactory: startupServiceFactory,
        deps: [StartupService],
        multi: true
    },
    ENV_PROVIDERS,
    APP_PROVIDERS,
    LoadingSpinnerService,
    EnrollmentProgramsService,
    EnrollmentService,
    AuthGuard,
    CanDeactivateStage,
    StageQuestionsService,
    UserResolver,
    AppStatementsResolver,
    DialogService,
    CardConfirmService,
    LaunchResolver,
    UserActivationResolver,
    PrintService,
    PrintGuard,
    GoogleAnalyticsService,
    ContractsService
  ]
})
export class AppModule {

  constructor(
    public appRef: ApplicationRef,
    public appState: AppState
  ) {}

  public hmrOnInit(store: StoreType) {
    if (!store || !store.state) {
      return;
    }
    //console.log('HMR store', JSON.stringify(store, null, 2));
    /**
     * Set state
     */
    this.appState._state = store.state;
    /**
     * Set input values
     */
    if ('restoreInputValues' in store) {
      let restoreInputValues = store.restoreInputValues;
      setTimeout(restoreInputValues);
    }

    this.appRef.tick();
    delete store.state;
    delete store.restoreInputValues;
  }

  public hmrOnDestroy(store: StoreType) {
    const cmpLocation = this.appRef.components.map((cmp) => cmp.location.nativeElement);
    /**
     * Save state
     */
    const state = this.appState._state;
    store.state = state;
    /**
     * Recreate root elements
     */
    store.disposeOldHosts = createNewHosts(cmpLocation);
    /**
     * Save input values
     */
    store.restoreInputValues  = createInputTransfer();
    /**
     * Remove styles
     */
    removeNgStyles();
  }

  public hmrAfterDestroy(store: StoreType) {
    /**
     * Display new elements
     */
    store.disposeOldHosts();
    delete store.disposeOldHosts;
  }

}



// WEBPACK FOOTER //
// ./src/app/app.module.ts